import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useSignal } from "@preact/signals";
import { useRef } from "react";
import head from "@tinkoff/utils/array/head";
import classNames from "classnames";
import asArray from "../../../lib/as-array";
import TextInput from "../TextInput";
import Divider from "../../Filter/Divider";
import MessageBox from "../../MessageBox";
import useElementBounds from "../../../lib/hooks/use-element-bounds";
import useDocumentSize from "../../../lib/hooks/use-document-size";
import useOnClickOutside from "../../../lib/hooks/use-on-click-outside";
import Button from "../../Button";
import style from "./style.module.scss";
export default function Select(props) {
    const { options, multiple = false, searchable, openList, error, width = 280, renderSelected, renderOption, renderGroupHeader, } = props;
    const allOptions = flattenOptions(options);
    const toggleExists = checkForToggleOptions(allOptions);
    const searchableList = typeof searchable !== "undefined" ? searchable : allOptions.length > 10;
    const searchTerm = useSignal("");
    const editWidth = width;
    const findMatchingOption = (value) => {
        return allOptions.find((option) => {
            return option.value === value;
        });
    };
    function convertStringsToOptions(values) {
        return values
            .map((option) => {
            if (typeof option === "string") {
                return findMatchingOption(option);
            }
            return option;
        })
            .filter(Boolean);
    }
    const value = props.value ? convertStringsToOptions(props.value) : undefined;
    const defaultValue = props.defaultValue ? convertStringsToOptions(props.defaultValue) : undefined;
    //Setup the initial values for the select component.
    const defaultValueUnvalidated = multiple ? asArray(defaultValue) : head(asArray(defaultValue));
    const defaultValueValidated = [];
    // Make sure the default(s) are present in the options and use the label for each item
    if (defaultValueUnvalidated && Array.isArray(defaultValueUnvalidated)) {
        defaultValueUnvalidated.forEach((option) => {
            const found = findMatchingOption(option.value);
            if (found) {
                defaultValueValidated.push(found);
            }
        });
    }
    else if (defaultValueUnvalidated) {
        const found = findMatchingOption(defaultValueUnvalidated.value);
        if (found) {
            defaultValueValidated.push(found);
        }
    }
    // Validate value options if controlled component.
    const validatedValue = [];
    if (value) {
        value.forEach((option) => {
            const found = findMatchingOption(option.value);
            if (found) {
                validatedValue.push(found);
            }
        });
    }
    const selected = useSignal(defaultValueValidated);
    const isOpen = useSignal(openList);
    const selectRef = useRef(null);
    const [selectListRef, selectListBounds] = useElementBounds();
    const docSize = useDocumentSize();
    // Close the select panel when the user clicks outside of it
    useOnClickOutside(selectRef, () => (isOpen.value = false));
    let selectListLeft = "0";
    if (selectListBounds.width + selectListBounds.left > docSize.width) {
        selectListLeft = `calc(${docSize.width}px - ${selectListBounds.left}px - ${selectListBounds.width}px - var(--grid-padding))`;
    }
    function editSelect() {
        isOpen.value = !isOpen.value;
    }
    const onOptionChange = (actionedSelectOption, optionSelected) => {
        let newSelectedOptions = value ? value : selected.value;
        if (toggleExists) {
            //If the user clicked on a toggle then update the selection.
            if (actionedSelectOption.toggle) {
                //Clear out any other selections as user is selecting a toggle value.
                newSelectedOptions = [];
            }
            else {
                //User has selected a different option from the toggle. So make sure we set any toggle values to off.
                newSelectedOptions = newSelectedOptions.filter((option) => {
                    return option.toggle !== true;
                });
            }
        }
        if (!multiple) {
            newSelectedOptions = [actionedSelectOption];
        }
        else if (optionSelected && multiple) {
            //User wanting to add the option to the selected options array.
            newSelectedOptions = [...newSelectedOptions, actionedSelectOption];
        }
        else if (!optionSelected && multiple) {
            //User wanting to remove the option from the selected options array.
            newSelectedOptions = newSelectedOptions.filter((option) => {
                return option.value !== actionedSelectOption.value;
            });
        }
        selected.value = newSelectedOptions;
        // Note these access the `props` object to help TS derive the type of `onChange`
        if (props.onChange) {
            if (props.multiple) {
                props.onChange(newSelectedOptions);
            }
            else {
                props.onChange(newSelectedOptions[0]);
            }
        }
        if (!multiple) {
            isOpen.value = false;
        }
    };
    const getOptions = (optionsOrGroup) => {
        if (isGroupArray(optionsOrGroup)) {
            return optionsOrGroup
                .map((group) => (Object.assign(Object.assign({}, group), { options: filterOptions(group.options, searchTerm.value) })))
                .filter((group) => group.options.length)
                .map((group, index) => {
                //What group header do we show.
                const groupHeaderDynamic = [];
                if (group.title && !renderGroupHeader) {
                    groupHeaderDynamic.push(_jsx("div", { className: style["select-list__group-header"], children: group.title }));
                }
                return (_jsxs(_Fragment, { children: [index > 0 && _jsx(Divider, {}), group.title && renderGroupHeader ? renderGroupHeader(group.title) : groupHeaderDynamic, group.options.map((option) => {
                            const optionSelected = isOptionSelected(option, value ? value : selected.value);
                            return (_jsx(Button, { onClick: () => onOptionChange(option, !optionSelected), variation: "none", className: style["select-list__button"], children: renderOption(option, optionSelected) }, option.value));
                        })] }));
            });
        }
        return (_jsx(_Fragment, { children: filterOptions(optionsOrGroup, searchTerm.value).map((option) => {
                const optionSelected = isOptionSelected(option, value ? value : selected.value);
                return (_jsx(Button, { onClick: () => onOptionChange(option, !optionSelected), variation: "none", className: style["select-list__button"], children: renderOption(option, optionSelected) }, option.value));
            }) }));
    };
    return (_jsxs("div", { className: classNames(style["select-list"], "qa-select"), children: [_jsxs("div", { ref: selectRef, className: style["select-list__container"], children: [_jsx("div", { className: style["select-list__selected-container"], children: renderSelected(value ? validatedValue : selected.value, editSelect) }), _jsx("div", { className: style["select-list__edit-panel-container"], children: isOpen.value ? (_jsx("div", { className: style["select-list__edit-panel"], style: { left: selectListLeft, width: editWidth }, ref: selectListRef, children: _jsxs("div", { children: [searchableList ? (_jsx("div", { className: style["select-list__search"], children: _jsx(TextInput, { placeholder: "Search", onInput: (event) => (searchTerm.value = event.currentTarget.value), clearable: true, spellCheck: false, defaultValue: searchTerm.value }) })) : null, _jsx("div", { tabIndex: -1, className: style["select-list__options"], children: getOptions(options) })] }) })) : null })] }), error ? _jsx(MessageBox, { type: "error", children: error }) : null] }));
}
/**
 * Filter an array of options based on a search term
 */
function filterOptions(options, searchTerm) {
    if (searchTerm === "") {
        return options;
    }
    else {
        return options.filter((option) => {
            if (option.label) {
                return option.label.toLowerCase().includes(searchTerm.toLowerCase());
            }
            else {
                return false;
            }
        });
    }
}
/**
 * Determine whether an option is a SelectOptionGroup
 */
export function isGroup(option) {
    return "options" in option;
}
/**
 * Determine whether an array of options is an array of SelectOptionGroup.
 * Required due to TS issue with .every()
 * @see https://github.com/microsoft/TypeScript/issues/44373
 */
export function isGroupArray(options) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return options.every(isGroup);
}
/**
 * Collapse an array of select options, grouped or not, into a flat Array<SelectOption>
 */
function flattenOptions(options) {
    return options.flatMap((option) => {
        if (isGroup(option)) {
            return option.options;
        }
        return option;
    }, []);
}
/**
 *  Is an option selected or not.
 */
function isOptionSelected(option, selected) {
    return selected.some((selectedOption) => {
        return option.value === selectedOption.value;
    });
}
/**
 * Check if any of the options are toggle options.
 */
function checkForToggleOptions(options) {
    return options.some((option) => {
        return option.toggle === true;
    });
}
